import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { usePayer } from 'src/state/payer';
import { useHeaderMenu } from 'src/state/headerMenu';
import { usercache } from '@sde/widgets-core/src/redux/tools/usercache';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Text } from '@uhg-abyss/web/ui/Text';
import { event } from '@uhg-abyss/web/tools/event';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { PayerSelectWidget } from './PayerSelectWidget';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { usePayers } from '@sde/widgets-core/src/redux/resources/payers';

const FlexNoWrap = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  marginTop: '7px',
});

const StrongText = styled('strong', {
  fontSize: '14px',
});

const NoOverflowPopover = styled(Popover, {
  '&.abyss-popover-content-container': {
    overflow: 'hidden !important',
  },
});
const ThinBorderButton = styled(Button, {
  '&.abyss-button-root': {
    borderWidth: '1px',
  },
});

const StyledDropdownArrow = styled(IconMaterial, {
  position: 'absolute',
  top: 3,
  right: 2,
});

const AbsoluteDivAtBottomOfDropdown = styled('div', {
  position: 'absolute',
  bottom: '0px',
  right: '0px',
});

export const PayerSelect = () => {
  const { payer } = usePayer.state();
  const { setPayer } = usePayer.actions();
  const { user } = useUser.state();
  const { getPayers } = usePayers.actions();
  const { isPayerOpen } = useHeaderMenu.state();
  const { togglePayer } = useHeaderMenu.actions();
  const { payers } = usePayers.state();
  const cachedPayer = usercache.get('payer');
  const { getLocation } = useRouter();
  const location = getLocation();
  const applicationName = location.pathname.split('/')[1];

  const payerSelectProps = {
    data: {
      title: 'Selected Payer',
      payer,
      payers,
      payerId: payer.payerId,
      application: ['referral', 'feeschedulelookup'].includes(applicationName)
        ? applicationName
        : '',
    },
    events: {
      onChange: (data) => {
        event.setData({
          payerId: data.payerId,
          payerName: data.payerName,
          payerLabel: data.payerLabel,
        });
      },
      onSave: (data) => {
        togglePayer(false);
        setPayer(data);
      },
      onError: () => {},
      onCancel: () => {
        togglePayer(false);
      },
    },
  };

  const setSelectedPayer = (payerId, list) => {
    let selectedPayer;
    if (payerId) {
      selectedPayer = list.find((p) => p && p.id === payerId);
    }

    if (!selectedPayer) {
      const [firstPayer] = list;
      selectedPayer = firstPayer;
    }

    const newSelectedPayer = selectedPayer
      ? {
          payerId: selectedPayer.id,
          payerName: selectedPayer.name,
          payerLabel: selectedPayer.label,
        }
      : null;
    if (payers.length > 1) {
      usercache.set('payer', newSelectedPayer);
    }

    setPayer(newSelectedPayer);
  };

  useEffect(() => {
    if (cachedPayer) {
      payerSelectProps.events.onChange(cachedPayer);
    }
    getPayers(
      {
        application: payerSelectProps.data.application,
        isIntakeUser: user.isIntakeUser,
      },
      (payersData) => {
        payerSelectProps.payers = payersData;
      },
      (err) => {
        payerSelectProps.events.onError(err);
      }
    );
  }, [payerSelectProps.data.application]);

  useEffect(() => {
    if (payers.length === 1 && payers[0]) {
      setSelectedPayer(payers[0].id, payers);
    }
    if (payers.length > 0) {
      const cachedPayerId = cachedPayer && cachedPayer.payerId;
      setSelectedPayer(cachedPayerId, payers);
    } else {
      getPayers();
    }
  }, [payers]);

  return (
    <NoOverflowPopover
      showClose={false}
      width={550}
      position="bottom"
      align="center"
      open={isPayerOpen}
      onOpenChange={togglePayer}
      content={
        <PayerSelectWidget
          payer={payer}
          setPayer={setPayer}
          isDropdownHidden
          payerSelectProps={payerSelectProps}
        />
      }
    >
      <Text size={14} fontWeight={600}>
        Payer{' '}
      </Text>
      <ThinBorderButton
        size="$sm"
        data-testid="payer-menu"
        variant="outline"
        after={
          !payer.payerLabel ? (
            <React.Fragment>
              <LoadingSpinner
                size="$sm"
                isLoading={!payer.payerLabel}
                ariaLoadingLabel="Loading Providers"
              />
              <IconMaterial icon="keyboard_arrow_down" color="$primary1" />
            </React.Fragment>
          ) : (
            <IconMaterial icon="keyboard_arrow_down" color="$primary1" />
          )
        }
      >
        <Choose>
          <When condition={!payer.payerLabel}> Select... </When>
          <Otherwise>
            <StrongText data-testid="payer-label">
              {payer.payerLabel}
            </StrongText>
          </Otherwise>
        </Choose>
      </ThinBorderButton>
    </NoOverflowPopover>
  );
};
